import { PATHS } from '@/routes/paths'

/** Preferred public site origin for canonicals / OG (matches FRONTEND_URL). */
export const SITE_ORIGIN = 'https://www.qbcon.in'

export function getSiteOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin.replace(/\/$/, '')
    // Local / preview hosts keep their origin; production always uses www.
    if (/localhost|127\.0\.0\.1|:\d+$/i.test(origin) || /shreejatechnology/i.test(origin)) {
      return origin
    }
    if (/qbcon\.in$/i.test(new URL(origin).hostname)) {
      return SITE_ORIGIN
    }
    return origin
  }
  return SITE_ORIGIN
}

export function absoluteUrl(path: string): string {
  const origin = getSiteOrigin()
  if (!path) return origin
  if (/^https?:\/\//i.test(path)) {
    try {
      const url = new URL(path)
      if (/^(www\.)?qbcon\.in$/i.test(url.hostname)) {
        url.protocol = 'https:'
        url.hostname = 'www.qbcon.in'
        url.hash = ''
        return url.toString().replace(/\/$/, url.pathname === '/' ? '/' : '')
      }
    } catch {
      return path
    }
    return path
  }
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

/** True when URL points at this property detail path (www / non-www both OK). */
export function isValidPropertyCanonical(url: string, slug: string): boolean {
  const expected = propertyDetailPath(slug).replace(/\/+$/, '')
  try {
    const path = new URL(url).pathname.replace(/\/+$/, '') || '/'
    return path === expected || path.endsWith(`/property/${slug}`)
  } catch {
    return false
  }
}

export function getDefaultOgImage(): string {
  return absoluteUrl('/logoqbc.webp')
}

/** Match og:image:type to the actual file so social crawlers can render the preview. */
export function ogImageMimeType(url?: string | null): string {
  const pathname = (url ?? '').split('?')[0].toLowerCase()
  if (pathname.endsWith('.webp')) return 'image/webp'
  if (pathname.endsWith('.png')) return 'image/png'
  if (pathname.endsWith('.gif')) return 'image/gif'
  if (pathname.endsWith('.svg')) return 'image/svg+xml'
  if (pathname.endsWith('.jpg') || pathname.endsWith('.jpeg')) return 'image/jpeg'
  return 'image/jpeg'
}

export function titleCase(slug?: string | null): string | null {
  if (!slug) return null
  return slug
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function cleanSeoText(raw: string): string {
  return raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/[#*_`~>|[\](){}]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Turn property body text into a readable meta description:
 * bullets → commas, tidy spacing, sentence case, 140–160 chars.
 */
export function polishPropertyMetaDescription(raw: string): string {
  let text = raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/\r\n?/g, '\n')
    .replace(/:\s*[-•]\s*/g, ': ')
    .replace(/\n\s*[-•]\s*/g, ', ')
    .replace(/\s*[-•]\s+/g, ', ')
    .replace(/[#*_`~>|[\](){}]/g, ' ')
    .replace(/\s*,\s*,+/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.])/g, '$1')
    .trim()

  if (!text) return ''

  text = text.charAt(0).toUpperCase() + text.slice(1)
  if (text.length > 40 && !/[.!?]$/.test(text)) {
    text += '.'
  }

  return truncateMetaDescription(text)
}

/** Meta description 140–160 chars; never cut mid-word when possible. */
export function truncateMetaDescription(text: string, min = 140, max = 160): string {
  const cleaned = cleanSeoText(text)
  if (cleaned.length <= max) return cleaned
  const slice = cleaned.slice(0, max)
  const lastSpace = slice.lastIndexOf(' ')
  if (lastSpace >= min) return slice.slice(0, lastSpace).trim()
  return slice.trim()
}

export function buildRobotsContent(noIndex: boolean, noFollow: boolean): string {
  const parts: string[] = []
  parts.push(noIndex ? 'noindex' : 'index')
  parts.push(noFollow ? 'nofollow' : 'follow')
  if (!noIndex && !noFollow) {
    parts.push('max-image-preview:large', 'max-snippet:-1', 'max-video-preview:-1')
  }
  return parts.join(', ')
}

export function propertyDetailPath(slug: string): string {
  return PATHS.propertyDetail(slug)
}

export function listingCanonical(pathname = '/properties', search = ''): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  params.delete('page')
  const qs = params.toString()
  return absoluteUrl(qs ? `${pathname}?${qs}` : pathname)
}

export function uniqueKeywords(parts: Array<string | null | undefined>): string {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of parts) {
    if (!part) continue
    for (const token of part.split(',')) {
      const key = token.trim().toLowerCase()
      if (!key || seen.has(key)) continue
      seen.add(key)
      out.push(token.trim())
    }
  }
  return out.join(', ')
}

export function parseSchemaJson(raw?: string | null): Record<string, unknown> | undefined {
  if (!raw?.trim()) return undefined
  try {
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    return undefined
  }
  return undefined
}

/** Browser tab titles use a normal hyphen, not an em dash. */
export function normalizeSeoTitle(title: string): string {
  return title.replace(/\s*—\s*/g, ' - ')
}
